<template>
  <el-drawer
    :title="t('member.signatureRecord.detail.title')"
    :visible="show"
    :append-to-body="true"
    destroy-on-close
    size="600px"
    @close="$emit('close')"
  >
    <FlexLayout v-loading="loading" col justify="between" class="content-wrapper">
      <div class="flex-1 overflow-auto">
        <BobDisplayForm
          :config="displayFormConfig"
          :fields="basicInfoFields"
        />

        <FlexLayout :gap="12" justify="end" class="mt-[40px]">
          <BaseElButton
            v-if="showControl.copyLink"
            plain
            :disabled="showControl.copyLinkDisabled"
            @click="copySignatureLink(recordData.id)"
          >
            {{ t('member.signatureRecord.detail.copyLink') }}
          </BaseElButton>
          <BaseElButton
            v-if="showControl.download"
            plain
            @click="onPreviewPdf"
          >
            {{ t('member.signatureRecord.detail.viewPdf') }}
          </BaseElButton>
          <BaseElButton
            v-if="showControl.download"
            plain
            @click="onDownloadPdf"
          >
            {{ t('member.signatureRecord.detail.downloadPdf') }}
          </BaseElButton>
        </FlexLayout>
      </div>

      <FlexLayout justify="end" class="mt-[24px]">
        <BaseElButton plain @click="$emit('close')">{{ t('member.signatureRecord.detail.close') }}</BaseElButton>
      </FlexLayout>
    </FlexLayout>
  </el-drawer>
</template>

<script>
import { computed, defineComponent, onBeforeMount, ref } from 'vue'
import { get } from 'lodash'
import Tag from '@/components/Tag/Tag.vue'
import BobDisplayForm from '@/components/BobDisplayForm.vue'
import { useShop } from '@/use/shop'
import { useSignatureRecordUtils } from '@/use/useSignatureRecord'
import { formatDate } from '@/utils/date'
import { FindSignatureRecordMapping, GetSignatureRecordMappingSignedFile } from '@/api/signatureRecordMapping'
import { signatureRecordStatusConfig, signatureSourceTypeConfig } from '@/config/signatureRecordMapping'
import FlexLayout from '@/components/FlexLayout.vue'
import { t } from '@/plugins/i18n/i18n'

export default defineComponent({
  name: 'SignatureRecordDetailDrawer',
  components: {
    BobDisplayForm,
    FlexLayout,
  },
  props: {
    recordId: {
      type: String,
      required: true,
    },
  },
  emits: ['close', 'refresh'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { getOrderCode, getCopyLinkStatus, copySignatureLink } = useSignatureRecordUtils()
    const show = ref(true)
    const recordData = ref({})
    const loading = ref(false)

    const fetchDetail = async () => {
      loading.value = true
      try {
        const [res, errMsg] = await FindSignatureRecordMapping({
          shopId: shopId.value,
          id: props.recordId,
        })

        if (errMsg) {
          window.$message.error(errMsg)
          emit('close')
          return
        }

        recordData.value = res || {}
      } finally {
        loading.value = false
      }
    }

    const displayFormConfig = computed(() => ({
      labelWidth: '120px',
      rowGap: 12,
      lineHeight: '32px',
      align: 'center',
    }))

    const basicInfoFields = computed(() => {
      const sourceType = get(recordData.value, 'sourceType')
      const status = get(recordData.value, 'status')

      return [
        {
          prop: 'memberName',
          label: t('member.signatureRecord.detail.memberName'),
          body: {
            component: 'span',
            render: () => get(recordData.value, 'Member.UserInfo.name', '-'),
          },
        },
        {
          prop: 'memberPhone',
          label: t('member.signatureRecord.detail.memberPhone'),
          body: {
            component: 'span',
            render: () => get(recordData.value, 'Member.UserInfo.phone', '-'),
          },
        },
        {
          prop: 'templateName',
          label: t('member.signatureRecord.detail.templateName'),
          body: {
            component: 'span',
            render: () => get(recordData.value, 'SignatureTemplate.name', '-'),
          },
        },
        {
          prop: 'sourceType',
          label: t('member.signatureRecord.detail.sourceType'),
          body: {
            component: 'span',
            render: () => {
              const labelKey = get(signatureSourceTypeConfig, `${sourceType}.label`)
              return labelKey ? t(labelKey) : '-'
            },
          },
        },
        {
          prop: 'orderCode',
          label: t('member.signatureRecord.detail.orderCode'),
          body: {
            component: 'span',
            render: () => getOrderCode(recordData.value),
          },
        },
        {
          prop: 'status',
          label: t('member.signatureRecord.detail.status'),
          body: {
            component: Tag,
            props: {
              type: get(signatureRecordStatusConfig, `${status}.tagType`, 'info'),
            },
            render: () => {
              const labelKey = get(signatureRecordStatusConfig, `${status}.label`)
              return labelKey ? t(labelKey) : '-'
            },
          },
        },
        {
          prop: 'signedAt',
          label: t('member.signatureRecord.detail.signedAt'),
          body: {
            component: 'span',
            render: () => get(recordData.value, 'SignatureRecord.effectiveAt')
              ? formatDate(get(recordData.value, 'SignatureRecord.effectiveAt'), 'YYYY/MM/DD HH:mm')
              : '-',
          },
        },
        {
          prop: 'signDeadline',
          label: t('member.signatureRecord.detail.signDeadline'),
          body: {
            component: 'span',
            render: () => get(recordData.value, 'signDeadline')
              ? formatDate(get(recordData.value, 'signDeadline'), 'YYYY/MM/DD HH:mm')
              : '-',
          },
        },
      ]
    })

    const showControl = computed(() => {
      const status = get(recordData.value, 'status')
      const copyLinkStatus = getCopyLinkStatus(recordData.value)

      return {
        copyLink: copyLinkStatus.canShow,
        copyLinkDisabled: copyLinkStatus.isDisabled,
        download: status === 'signed',
      }
    })

    /** 取得已簽署 PDF 的下載 URL */
    const getSignedFileUrl = async () => {
      const [res, errMsg] = await GetSignatureRecordMappingSignedFile({
        shopId: shopId.value,
        id: props.recordId,
      })

      if (errMsg) {
        window.$message.error(errMsg)
        return null
      }

      const downloadUrl = get(res, 'downloadUrl')
      if (!downloadUrl) {
        window.$message.error(t('member.signatureRecord.detail.cannotGetPdf'))
        return null
      }

      return downloadUrl
    }

    /** 新分頁檢視 PDF */
    const onPreviewPdf = async () => {
      const url = await getSignedFileUrl()
      if (url) window.open(url, '_blank')
    }

    /** 下載 PDF 檔案 */
    const onDownloadPdf = async () => {
      const url = await getSignedFileUrl()
      if (!url) return

      const templateName = get(recordData.value, 'SignatureTemplate.name', t('member.signatureRecord.detail.templateName'))
      const memberName = get(recordData.value, 'Member.UserInfo.name', t('member.signatureRecord.detail.memberName'))
      const signedAt = get(recordData.value, 'SignatureRecord.effectiveAt')
      const dateStr = signedAt ? formatDate(signedAt, 'YYYY-MM-DD') : ''
      const filename = `${templateName}_${memberName}_${dateStr}.pdf`

      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = filename
          link.click()
          URL.revokeObjectURL(link.href)
        })
        .catch(() => {
          window.$message.error(t('member.signatureRecord.detail.downloadFailed'))
        })
    }

    onBeforeMount(async () => {
      await fetchDetail()
    })

    return {
      t,
      show,
      displayFormConfig,
      basicInfoFields,
      showControl,
      copySignatureLink,
      onPreviewPdf,
      onDownloadPdf,
      recordData,
      loading,
    }
  },
})
</script>

<style lang="postcss" scoped>
.content-wrapper {
  @apply px-[24px] h-full;
}
</style>
